import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { User, Users, Building, Calendar, ArrowRight } from 'lucide-react';
import { investorTypes } from '../data/mock';
import { Card } from './ui/card';
import { Button } from './ui/button';

const iconMap = {
  User: User,
  Users: Users,
  Building: Building
};

const FundTermsSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const fundDetails = [
    { value: '$25M', label: 'Target Raise', icon: '💰', subtext: '' },
    { value: '20-28%', label: 'Target IRR', icon: '📈', subtext: '20% Carry' },
    { value: '10%', label: 'Preferred Return', icon: '🎯', subtext: 'Delaware LP' },
    { value: '5-7 Years', label: 'Hold Period', icon: '⏱️', subtext: '' }
  ];

  return (
    <section id="fund-terms" className="relative gradient-section py-16 lg:py-24 overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-40 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-[120px] animate-float" />
      <div className="absolute bottom-20 left-0 w-80 h-80 bg-[#1a2940]/50 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-[7.6923%]">
        {/* Section Header */}
        <motion.div 
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-14"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#B8962E] rounded-full mb-5 shadow-lg shadow-[#D4AF37]/20"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            <span className="text-label text-[#0A1628]">506(c) Private Placement</span>
          </motion.div>
          <motion.h2 
            className="heading-h2 mb-4"
            variants={itemVariants}
          >
            Fund Terms &
            <span className="gradient-text"> Structure</span>
          </motion.h2>
          <motion.p 
            className="text-body"
            variants={itemVariants}
          >
            Aligned interests with institutional-quality terms designed for accredited investors 
            seeking value-add real estate investments in the Greater Houston market.
          </motion.p>
        </motion.div>

        {/* Fund Details Grid - 4 Cards with Icons (Fee structure integrated) */}
        <motion.div 
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          {fundDetails.map((detail, index) => (
            <motion.div 
              key={index}
              className="glass-strong border border-white/10 rounded-2xl p-6 text-center group"
              variants={itemVariants}
              whileHover={{ 
                y: -8, 
                borderColor: 'rgba(212, 175, 55, 0.4)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.3), 0 0 30px rgba(212,175,55,0.1)' 
              }}
            >
              <motion.div 
                className="w-14 h-14 bg-[#1a2940] rounded-2xl flex items-center justify-center mx-auto mb-5 border border-[#D4AF37]/20"
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                <span className="text-2xl">{detail.icon}</span>
              </motion.div>
              <motion.span 
                className="gradient-text text-3xl lg:text-4xl font-bold block mb-2"
                whileHover={{ scale: 1.05 }}
              >
                {detail.value}
              </motion.span>
              <span className="text-white/50 text-sm block">{detail.label}</span>
              {detail.subtext && (
                <span className="text-[#D4AF37]/70 text-xs mt-1 block">{detail.subtext}</span>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* View Full Fund Terms Button */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/fund-terms">
            <Button 
              className="group bg-transparent border-2 border-[#D4AF37]/40 hover:border-[#D4AF37] text-white font-semibold px-8 py-3 h-12 rounded-full text-sm transition-all duration-300 inline-flex items-center gap-2"
              data-testid="view-fund-terms-btn"
            >
              View Full Fund Terms & Structure
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Button>
          </Link>
        </motion.div>

        {/* Investor Types */}
        <motion.div 
          className="mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={containerVariants}
        >
          <motion.h3 
            className="text-white text-xl font-bold mb-6 text-center"
            variants={itemVariants}
          >
            Who We Serve
          </motion.h3>
          <div className="grid md:grid-cols-3 gap-6">
            {investorTypes.map((type, index) => {
              const IconComponent = iconMap[type.icon];
              const investorImages = [
                'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/ov7lgwja_3cb3b652-e6e0-417c-9b34-33a5ee73760a.png',
                'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/5va5y87g_779e104c-016e-4630-9928-435fbb244227.png',
                'https://customer-assets.emergentagent.com/job_propinvest-20/artifacts/zcpz8l8y_2b9b5a24-053d-4eb2-a89b-a845a981f394.png'
              ];
              return (
                <motion.div
                  key={type.id}
                  variants={itemVariants}
                >
                  <Card className="group card-modern rounded-2xl h-full overflow-hidden">
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <motion.img 
                        src={investorImages[index]}
                        alt={type.title}
                        className="w-full h-full object-cover"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-transparent opacity-60" />
                      {/* Icon Badge */}
                      <div className="absolute bottom-4 left-4">
                        <motion.div 
                          className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-xl flex items-center justify-center shadow-lg shadow-[#D4AF37]/30"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        >
                          <IconComponent className="h-6 w-6 text-[#0A1628]" />
                        </motion.div>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="p-6">
                      <h4 className="text-white text-xl font-semibold mb-3">{type.title}</h4>
                      <p className="text-white/50 text-base leading-relaxed mb-4">{type.description}</p>
                      <div className="pt-4 border-t border-white/10">
                        <span className="text-white/40 text-xs uppercase tracking-wider">Minimum Investment</span>
                        <p className="gradient-text text-lg font-bold">{type.minInvestment}</p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Link 
            to="/contact"
            className="block w-full sm:inline-block sm:w-auto"
          >
            <button 
              className="cta-contact px-6 sm:px-10 py-3 sm:py-4 h-12 sm:h-14 rounded-full text-sm sm:text-base w-full sm:w-auto"
              data-testid="fund-terms-schedule-consultation-btn"
            >
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
              <span className="whitespace-nowrap">Schedule Consultation</span>
            </button>
          </Link>
          <p className="text-white/30 text-xs sm:text-sm mt-4">
            Available to verified accredited investors only
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FundTermsSection;
